/**
 * Phase 4 slice 3 — NCR list. Filters + raise button + click-through to detail.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileWarning } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const CATEGORIES = ['Material', 'Workmanship', 'Design Deviation', 'Documentation', 'Process', 'Test Failure', 'Other'];
const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  investigating: 'bg-amber-50 text-amber-700 border-amber-200',
  capa_pending: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  waived: 'bg-slate-100 text-slate-600 border-slate-200',
};
import { formatDate as fmt } from '../../lib/format';

export default function NCRList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);

  useEffect(() => {
    const q = [projectId && `project_id=${projectId}`, status && `status=${status}`, severity && `severity=${severity}`, category && `category=${encodeURIComponent(category)}`].filter(Boolean).join('&');
    apiGet(`/quality/ncrs${q ? `?${q}` : ''}`).then((d) => setRows(d.ncrs || [])).catch(() => {});
  }, [projectId, status, severity, category]);

  return (
    <div>
      <PageHeader overline="Quality" title="Non-Conformance Reports" subtitle="Raise → investigate → CAPA → verify → close"
        actions={(
          <button onClick={() => navigate('/app/quality/ncrs/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="ncr-raise-nav">
            <Plus size={14} /> Raise NCR
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]" data-testid="ncr-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="ncr-filter-status">
          <option value="">All statuses</option>
          {['open', 'investigating', 'capa_pending', 'closed', 'waived'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="ncr-filter-severity">
          <option value="">All severities</option>
          {['low', 'medium', 'high', 'critical'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="ncr-filter-category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="ncr-table">
            <thead><tr><th>NCR No.</th><th>Title</th><th>Category</th><th>Severity</th><th>Source</th><th>Raised</th><th>Status</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10"><FileWarning size={20} className="inline mr-2 opacity-40" />No NCRs found.</td></tr>
                : rows.map((n) => (
                  <tr key={n.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/ncrs/${n.id}`)} data-testid={`ncr-row-${n.id}`}>
                    <td className="text-xs font-display font-bold text-[#3b4872]">{n.ncr_no}</td>
                    <td className="text-[13px] font-display font-semibold max-w-[260px] truncate">{n.title}</td>
                    <td className="text-xs text-slate-600">{n.category}</td>
                    <td><span className={`tag border ${SEV_TONE[n.severity]}`}>{n.severity}</span></td>
                    <td className="text-xs text-slate-500">{n.source}</td>
                    <td className="text-xs text-slate-500">{fmt(n.raised_at)}</td>
                    <td><span className={`tag border ${STATUS_TONE[n.status]}`}>{(n.status || '').replace('_', ' ')}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
